import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  User, Calendar, MapPin, Flame, ArrowRight, ArrowLeft, CheckCircle, 
  Stethoscope, Hospital, UserPlus, FileText, Heart, Pill, AlertTriangle, 
  Users, Activity, Camera
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ComprehensiveSessionForm = ({ onSessionCreated }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    // Consulting Physician
    physician_name: '',
    physician_credentials: 'MD, FRCS',
    clinic_name: '',
    clinic_address: '',
    clinic_city: '',
    clinic_province: '',
    clinic_postal_code: '',
    clinic_phone: '',
    clinic_fax: '',
    clinic_email: '',
    
    // Enhanced Patient Information
    patient_name: '',
    patient_id: '',
    date_of_birth: '',
    health_card_number: '',
    date_of_consultation: new Date().toISOString().split('T')[0],
    referring_physician: '',
    source_of_referral: '',
    
    // History of Present Illness (HPI)
    injury_date: '',
    injury_time: '',
    burn_mechanism: '',
    injury_circumstances: '',
    initial_treatment: '',
    associated_injuries: '',
    tetanus_status: '',
    pain_score: '',
    photos_available: 'no',
    
    // Past Medical History
    past_medical_history: '',
    past_surgical_history: '',
    current_medications: '',
    allergies: '',
    
    // Social History  
    smoking_status: 'none',
    smoking_pack_years: '',
    alcohol_use: 'none',
    alcohol_details: '',
    recreational_drugs: 'none',
    drug_details: '',
    occupation: '',
    living_situation: '',
    
    // Physical Examination - General
    vital_bp: '',
    vital_hr: '',
    vital_rr: '',
    vital_temp: '',
    vital_spo2: '',
    general_appearance: '',
    
    // Physical Examination - Burn Assessment
    burn_location: '',
    burn_tbsa: '',
    burn_depth: '',
    burn_description: '',
    infection_signs: '',
    burn_comments: '',
    wound_edges: '',
    eschar_blistering: '',
    odour_drainage: '',
    
    // Systemic Examination
    cardiovascular_exam: '',
    respiratory_exam: '',
    abdomen_exam: '',
    extremities_exam: '',
    neurologic_exam: '',
    
    // Investigations
    cbc_results: '',
    liver_glucose_results: '',
    cultures_results: '',
    imaging_results: ''
  });

  const steps = [
    {
      number: 1,
      title: "Physician & Patient Info",
      icon: <UserPlus className="w-5 h-5" />,
      description: "Basic consultation details"
    },
    {
      number: 2,
      title: "Medical History",
      icon: <FileText className="w-5 h-5" />,
      description: "HPI, medical background"
    },
    {
      number: 3,
      title: "Social & Physical Exam",
      icon: <Stethoscope className="w-5 h-5" />,
      description: "Social history & examination"
    }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.physician_name && formData.patient_name && formData.patient_id && 
               formData.date_of_consultation && formData.source_of_referral;
      case 2:
        return formData.injury_date && formData.burn_mechanism && formData.burn_location;
      case 3:
        return formData.burn_tbsa && formData.burn_depth;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    } else {
      alert('Please fill in all required fields before continuing.');
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) {
      alert('Please complete all required fields.');
      return;
    }
    
    setLoading(true);
    
    try {
      // Create patient first
      const patientData = {
        patient_id: formData.patient_id,
        name: formData.patient_name,
        date_of_birth: formData.date_of_birth,
        health_card_number: formData.health_card_number
      };
      
      await axios.post(`${API}/patients`, patientData);
      
      // Create comprehensive session
      const sessionData = {
        ...formData,
        // Legacy fields for compatibility
        burn_cause: formData.burn_mechanism,
        burn_location: formData.burn_location,
        injury_date: `${formData.injury_date}T${formData.injury_time || '12:00:00'}`
      };
      
      const response = await axios.post(`${API}/sessions`, sessionData);
      
      if (onSessionCreated) {
        onSessionCreated(response.data);
      }
    } catch (error) {
      console.error('Failed to create session:', error);
      alert('Failed to create session. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      {/* Consulting Physician */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Stethoscope className="w-5 h-5" />
            Consulting Physician Information
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="physician_name">Physician Name *</Label>
            <Input
              id="physician_name"
              placeholder="Dr. John Smith"
              value={formData.physician_name}
              onChange={(e) => handleChange('physician_name', e.target.value)}
              required
            />
          </div>
          <div>
            <Label htmlFor="physician_credentials">Credentials</Label>
            <Input
              id="physician_credentials"
              placeholder="MD, FRCS"
              value={formData.physician_credentials}
              onChange={(e) => handleChange('physician_credentials', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="clinic_name">Clinic/Hospital Name</Label>
            <Input
              id="clinic_name"
              placeholder="Toronto General Hospital"
              value={formData.clinic_name}
              onChange={(e) => handleChange('clinic_name', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="clinic_address">Address</Label>
            <Input
              id="clinic_address"
              placeholder="200 Elizabeth St"
              value={formData.clinic_address}
              onChange={(e) => handleChange('clinic_address', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="clinic_city">City</Label>
            <Input
              id="clinic_city"
              placeholder="Toronto"
              value={formData.clinic_city}
              onChange={(e) => handleChange('clinic_city', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="clinic_province">Province</Label>
            <Input
              id="clinic_province"
              placeholder="ON"
              value={formData.clinic_province}
              onChange={(e) => handleChange('clinic_province', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="clinic_phone">Phone</Label>
            <Input
              id="clinic_phone"
              placeholder="416-340-4800"
              value={formData.clinic_phone}
              onChange={(e) => handleChange('clinic_phone', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="clinic_email">Email</Label>
            <Input
              id="clinic_email"
              type="email"
              placeholder="doctor@hospital.ca"
              value={formData.clinic_email}
              onChange={(e) => handleChange('clinic_email', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Patient Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Patient Information
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="patient_name">Patient Name *</Label>
            <Input
              id="patient_name"
              placeholder="Jane Doe"
              value={formData.patient_name}
              onChange={(e) => handleChange('patient_name', e.target.value)}
              required
            />
          </div>
          <div>
            <Label htmlFor="patient_id">Patient ID *</Label>
            <Input
              id="patient_id"
              placeholder="P12345678"
              value={formData.patient_id}
              onChange={(e) => handleChange('patient_id', e.target.value)}
              required
            />
          </div>
          <div>
            <Label htmlFor="date_of_birth">Date of Birth</Label>
            <Input
              id="date_of_birth"
              type="date"
              value={formData.date_of_birth}
              onChange={(e) => handleChange('date_of_birth', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="health_card_number">Health Card Number</Label>
            <Input
              id="health_card_number"
              placeholder="1234-567-890"
              value={formData.health_card_number}
              onChange={(e) => handleChange('health_card_number', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="date_of_consultation">Date of Consultation *</Label>
            <Input
              id="date_of_consultation"
              type="date"
              value={formData.date_of_consultation}
              onChange={(e) => handleChange('date_of_consultation', e.target.value)}
              required
            />
          </div>
          <div>
            <Label htmlFor="referring_physician">Referring Physician</Label>
            <Input
              id="referring_physician"
              placeholder="Dr. Jane Smith"
              value={formData.referring_physician}
              onChange={(e) => handleChange('referring_physician', e.target.value)}
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="source_of_referral">Source of Referral *</Label>
            <Select value={formData.source_of_referral} onValueChange={(value) => handleChange('source_of_referral', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select referral source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="er">Emergency Room</SelectItem>
                <SelectItem value="family_md">Family Physician</SelectItem>
                <SelectItem value="hospital">Hospital</SelectItem>
                <SelectItem value="self">Self-Referral</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      {/* History of Present Illness */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Flame className="w-5 h-5" />
            History of Present Illness (HPI)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="injury_date">Date of Injury *</Label>
              <Input
                id="injury_date"
                type="date"
                value={formData.injury_date}
                onChange={(e) => handleChange('injury_date', e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="injury_time">Time of Injury</Label>
              <Input
                id="injury_time"
                type="time"
                value={formData.injury_time}
                onChange={(e) => handleChange('injury_time', e.target.value)}
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="burn_mechanism">Mechanism of Burn *</Label>
            <Select value={formData.burn_mechanism} onValueChange={(value) => handleChange('burn_mechanism', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select burn mechanism" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="flame">Flame</SelectItem>
                <SelectItem value="scald">Scald</SelectItem>
                <SelectItem value="contact">Contact</SelectItem>
                <SelectItem value="chemical">Chemical</SelectItem>
                <SelectItem value="electrical">Electrical</SelectItem>
                <SelectItem value="flash">Flash</SelectItem>
                <SelectItem value="radiation">Radiation</SelectItem>
                <SelectItem value="friction">Friction</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="burn_location">Burn Location *</Label>
            <Input
              id="burn_location"
              placeholder="Right forearm, palm"
              value={formData.burn_location}
              onChange={(e) => handleChange('burn_location', e.target.value)}
              required
            />
          </div>

          <div>
            <Label htmlFor="injury_circumstances">Circumstances of Injury</Label>
            <textarea
              id="injury_circumstances"
              className="w-full p-2 border rounded-md resize-none h-24"
              placeholder="Detailed description of how the injury occurred, location, activities, clothing ignition, etc."
              value={formData.injury_circumstances}
              onChange={(e) => handleChange('injury_circumstances', e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="initial_treatment">Initial Treatment Prior to Arrival</Label>
            <textarea
              id="initial_treatment"
              className="w-full p-2 border rounded-md resize-none h-20"
              placeholder="First aid, cooling, dressings, pain management, antibiotics administered"
              value={formData.initial_treatment}
              onChange={(e) => handleChange('initial_treatment', e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="associated_injuries">Associated Injuries/Symptoms</Label>
            <textarea
              id="associated_injuries"
              className="w-full p-2 border rounded-md resize-none h-20"
              placeholder="Inhalation injury, trauma, loss of consciousness, pain level"
              value={formData.associated_injuries}
              onChange={(e) => handleChange('associated_injuries', e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="tetanus_status">Tetanus Status</Label>
              <Input
                id="tetanus_status"
                placeholder="Up to date / Unknown"
                value={formData.tetanus_status}
                onChange={(e) => handleChange('tetanus_status', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="pain_score">Pain Score (0-10)</Label>
              <Input
                id="pain_score"
                type="number"
                min="0"
                max="10"
                placeholder="5"
                value={formData.pain_score}
                onChange={(e) => handleChange('pain_score', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="photos_available">Photos Available</Label>
              <Select value={formData.photos_available} onValueChange={(value) => handleChange('photos_available', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Medical Background */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Medical Background
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="past_medical_history">Past Medical History</Label>
            <textarea
              id="past_medical_history"
              className="w-full p-2 border rounded-md resize-none h-20"
              placeholder="Significant past medical conditions, chronic diseases, etc."
              value={formData.past_medical_history}
              onChange={(e) => handleChange('past_medical_history', e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="past_surgical_history">Past Surgical History</Label>
            <textarea
              id="past_surgical_history"
              className="w-full p-2 border rounded-md resize-none h-20"
              placeholder="Previous surgical procedures and dates"
              value={formData.past_surgical_history}
              onChange={(e) => handleChange('past_surgical_history', e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="current_medications">Current Medications</Label>
            <textarea
              id="current_medications"
              className="w-full p-2 border rounded-md resize-none h-20"
              placeholder="List current medications, dosages, and frequency"
              value={formData.current_medications}
              onChange={(e) => handleChange('current_medications', e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="allergies">Allergies</Label>
            <textarea
              id="allergies"
              className="w-full p-2 border rounded-md resize-none h-16"
              placeholder="Known allergies and reactions (NKDA if none)"
              value={formData.allergies}
              onChange={(e) => handleChange('allergies', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      {/* Social History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Social History
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="smoking_status">Smoking</Label>
              <Select value={formData.smoking_status} onValueChange={(value) => handleChange('smoking_status', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="occasional">Occasional</SelectItem>
                  <SelectItem value="regular">Regular</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.smoking_status === 'regular' && (
              <div>
                <Label htmlFor="smoking_pack_years">Pack-Years</Label>
                <Input
                  id="smoking_pack_years"
                  placeholder="10"
                  value={formData.smoking_pack_years}
                  onChange={(e) => handleChange('smoking_pack_years', e.target.value)}
                />
              </div>
            )}

            <div>
              <Label htmlFor="alcohol_use">Alcohol Use</Label>
              <Select value={formData.alcohol_use} onValueChange={(value) => handleChange('alcohol_use', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="occasional">Occasional</SelectItem>
                  <SelectItem value="regular">Regular</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.alcohol_use === 'regular' && (
              <div>
                <Label htmlFor="alcohol_details">Alcohol Details</Label>
                <Input
                  id="alcohol_details"
                  placeholder="2 drinks per week"
                  value={formData.alcohol_details}
                  onChange={(e) => handleChange('alcohol_details', e.target.value)}
                />
              </div>
            )}

            <div>
              <Label htmlFor="recreational_drugs">Recreational Drugs</Label>
              <Select value={formData.recreational_drugs} onValueChange={(value) => handleChange('recreational_drugs', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.recreational_drugs === 'yes' && (
              <div>
                <Label htmlFor="drug_details">Drug Details</Label>
                <Input
                  id="drug_details"
                  placeholder="Type and frequency"
                  value={formData.drug_details}
                  onChange={(e) => handleChange('drug_details', e.target.value)}
                />
              </div>
            )}

            <div>
              <Label htmlFor="occupation">Occupation</Label>
              <Input
                id="occupation"
                placeholder="Software Engineer"
                value={formData.occupation}
                onChange={(e) => handleChange('occupation', e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="living_situation">Living Situation / Family Support</Label>
            <textarea
              id="living_situation"
              className="w-full p-2 border rounded-md resize-none h-16"
              placeholder="Lives alone, with family, availability of support, etc."
              value={formData.living_situation}
              onChange={(e) => handleChange('living_situation', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Physical Examination */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Physical Examination
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <h4 className="font-semibold text-gray-700">General & Vitals</h4>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <Label htmlFor="vital_bp">BP</Label>
              <Input
                id="vital_bp"
                placeholder="120/80"
                value={formData.vital_bp}
                onChange={(e) => handleChange('vital_bp', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="vital_hr">HR</Label>
              <Input
                id="vital_hr"
                placeholder="72"
                value={formData.vital_hr}
                onChange={(e) => handleChange('vital_hr', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="vital_rr">RR</Label>
              <Input
                id="vital_rr"
                placeholder="16"
                value={formData.vital_rr}
                onChange={(e) => handleChange('vital_rr', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="vital_temp">Temp</Label>
              <Input
                id="vital_temp"
                placeholder="37.0"
                value={formData.vital_temp}
                onChange={(e) => handleChange('vital_temp', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="vital_spo2">SpO₂</Label>
              <Input
                id="vital_spo2"
                placeholder="98"
                value={formData.vital_spo2}
                onChange={(e) => handleChange('vital_spo2', e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="general_appearance">General Appearance / Pain Level / Mental Status</Label>
            <textarea
              id="general_appearance"
              className="w-full p-2 border rounded-md resize-none h-16"
              placeholder="Alert, oriented, appears comfortable, pain 3/10"
              value={formData.general_appearance}
              onChange={(e) => handleChange('general_appearance', e.target.value)}
            />
          </div>

          <h4 className="font-semibold text-gray-700 pt-4">Burn Assessment</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="burn_tbsa">TBSA % *</Label>
              <Input
                id="burn_tbsa"
                placeholder="5"
                value={formData.burn_tbsa}
                onChange={(e) => handleChange('burn_tbsa', e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="burn_depth">Burn Depth *</Label>
              <Select value={formData.burn_depth} onValueChange={(value) => handleChange('burn_depth', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select depth" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="superficial">Superficial</SelectItem>
                  <SelectItem value="partial_thickness">Partial-thickness</SelectItem>
                  <SelectItem value="full_thickness">Full-thickness</SelectItem>
                  <SelectItem value="mixed">Mixed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="infection_signs">Signs of Infection</Label>
              <Input
                id="infection_signs"
                placeholder="None"
                value={formData.infection_signs}
                onChange={(e) => handleChange('infection_signs', e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="burn_description">Burn Description</Label>
            <textarea
              id="burn_description"
              className="w-full p-2 border rounded-md resize-none h-20"
              placeholder="Detailed wound characteristics, appearance, etc."
              value={formData.burn_description}
              onChange={(e) => handleChange('burn_description', e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="wound_edges">Wound Edges / Wound Bed</Label>
              <Input
                id="wound_edges"
                placeholder="Well-defined, clean"
                value={formData.wound_edges}
                onChange={(e) => handleChange('wound_edges', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="odour_drainage">Odour / Drainage</Label>
              <Input
                id="odour_drainage"
                placeholder="None"
                value={formData.odour_drainage}
                onChange={(e) => handleChange('odour_drainage', e.target.value)}
              />
            </div>
          </div>

          <h4 className="font-semibold text-gray-700 pt-4">Systemic Examination</h4>
          <div className="space-y-3">
            <div>
              <Label htmlFor="cardiovascular_exam">Cardiovascular</Label>
              <Input
                id="cardiovascular_exam"
                placeholder="Regular rate and rhythm, no murmurs"
                value={formData.cardiovascular_exam}
                onChange={(e) => handleChange('cardiovascular_exam', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="respiratory_exam">Respiratory</Label>
              <Input
                id="respiratory_exam"
                placeholder="Clear to auscultation bilaterally"
                value={formData.respiratory_exam}
                onChange={(e) => handleChange('respiratory_exam', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="neurologic_exam">Neurologic</Label>
              <Input
                id="neurologic_exam"
                placeholder="Alert and oriented x3"
                value={formData.neurologic_exam}
                onChange={(e) => handleChange('neurologic_exam', e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Investigations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Investigations / Lab Work
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label htmlFor="cbc_results">CBC / Electrolytes / Renal Function</Label>
            <Input
              id="cbc_results"
              placeholder="WNL / Pending"
              value={formData.cbc_results}
              onChange={(e) => handleChange('cbc_results', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="cultures_results">Cultures / Swabs</Label>
            <Input
              id="cultures_results"
              placeholder="Wound culture sent"
              value={formData.cultures_results}
              onChange={(e) => handleChange('cultures_results', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="imaging_results">Imaging</Label>
            <Input
              id="imaging_results"
              placeholder="No imaging required"
              value={formData.imaging_results}
              onChange={(e) => handleChange('imaging_results', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderStepIndicator = () => (
    <div className="flex items-center justify-between mb-8">
      {steps.map((step, index) => (
        <div key={step.number} className="flex items-center">
          <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 
            ${currentStep >= step.number 
              ? 'bg-blue-600 border-blue-600 text-white' 
              : 'border-gray-300 text-gray-400'}`}>
            {currentStep > step.number ? <CheckCircle className="w-6 h-6" /> : step.icon}
          </div>
          <div className="ml-3 hidden md:block">
            <p className={`text-sm font-medium ${currentStep >= step.number ? 'text-blue-600' : 'text-gray-400'}`}>
              {step.title}
            </p>
            <p className="text-xs text-gray-500">{step.description}</p>
          </div>
          {index < steps.length - 1 && (
            <div className={`w-12 h-px mx-4 ${currentStep > step.number ? 'bg-blue-600' : 'bg-gray-300'}`} />
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="w-full max-w-6xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle className="text-center">
            <Hospital className="w-8 h-8 mx-auto mb-2" />
            Medical Burn Consultation - Patient Intake
          </CardTitle>
          {renderStepIndicator()}
        </CardHeader>

        <CardContent>
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            <Button
              variant="outline"
              onClick={prevStep}
              disabled={currentStep === 1}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Previous
            </Button>

            {currentStep < 3 ? (
              <Button
                onClick={nextStep}
                className="flex items-center gap-2"
              >
                Next
                <ArrowRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
              >
                {loading ? 'Creating...' : 'Create Consultation'}
                <CheckCircle className="w-4 h-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ComprehensiveSessionForm;